import type { ReactNode } from "react"

/*
 * The rules behind the Filter menu and its chips, kept apart from the components so they can be
 * checked without a browser.
 */

/**
 * Whether the Filter menu offers a facet. One every row has a value of narrows nothing with fewer
 * than two values, so it isn't offered. One that `narrowsWithOne`, as a Label only some rows carry
 * does, is offered once it has a value.
 */
export const isOffered = ({
  values,
  narrowsWithOne = false,
}: {
  values: readonly unknown[]
  narrowsWithOne?: boolean
}) => values.length >= (narrowsWithOne ? 1 : 2)

/** How a chip joins its field to its value: "is" for one value, "is any of" for several. */
export function operatorOf(mode: "single" | "multi", chosen: number) {
  return mode === "multi" && chosen > 1 ? "is any of" : "is"
}

/**
 * The values a chip names, in the order they were chosen. A chosen value no row has any more, as
 * from an old link, is named by `labelOf` when the facet can name any value of its own, and
 * otherwise by its id with a capital, so the chip still says what is filtering the list and reads
 * "Access is Read" rather than the URL's "read".
 */
export function chipValuesOf(
  values: readonly { id: string; label: string; glyph?: ReactNode }[],
  chosen: readonly string[],
  labelOf: (id: string) => string = capitalised,
) {
  return chosen.map((id) => {
    const value = values.find((each) => each.id === id)
    return value ? { label: value.label, glyph: value.glyph } : { label: labelOf(id) }
  })
}

const capitalised = (id: string) => id.charAt(0).toUpperCase() + id.slice(1)

/** A chip in words, as a screen reader hears it: "Area is any of Frontend, Backend". */
export function describeChip(
  field: string | undefined,
  operator: string | undefined,
  values: readonly { label: string }[],
) {
  return [field, operator, values.map(({ label }) => label).join(", ")].filter(Boolean).join(" ")
}

/**
 * How many of a row's chips stand whole in `room` px, `gap` px apart: all of them where they fit,
 * and otherwise as many as fit beside the "+n" chip that stands for the rest, `more` px with its
 * gap.
 */
export function chipsThatFit(widths: readonly number[], room: number, gap: number, more: number) {
  const whole = widths.reduce((sum, width) => sum + width, 0) + gap * Math.max(0, widths.length - 1)
  if (whole <= room) return widths.length
  let used = 0
  for (const [index, width] of widths.entries()) {
    const next = used + (index > 0 ? gap : 0) + width
    if (next + more > room) return index
    used = next
  }
  return widths.length
}
